import type {ReactElement} from 'react';
import React, {useEffect, useMemo, useState} from 'react';
import type {ShareDesignProps} from '@Panels/share-panel/components/share-design/share-design.types';
import {ShareViaEmail} from '@Panels/share-panel/components/share-via-email/share-via-email';
import type {ContributorEmailInvitationResponse, ContributorInfo, DesignContributorInfo, ShareDesignInfoResponse} from '@Panels/share-panel/share-panel.types';
import {DesignPermissionLevel, DesignPermissionLevelValue, SHARE_PANEL_ID, ShareItemType} from '@Panels/share-panel/share-panel.types';
import {ControlledListItemType3} from '@Components/controlled-list/components/controlled-list-item-type-3';
import {ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import {openErrorModal} from '@Modals/error-modal';
import styles from '@Panels/share-panel/share-panel.module.scss';
import {ShareViaLink} from '@Panels/share-panel/components/share-via-link/share-via-link';
import {
  getDesignPermissionLevelText,
  getInitialSharedInTeamBarState,
  getProfilePictureUrl,
  getShareErrorMessage,
  openPermissionDeniedPanel,
} from '@Panels/share-panel/share-panel-library';
import {Text, TextSize} from '@Components/text';
import Divider from '@Components/divider';
import type {ControlledListItemType2Props} from '@Components/controlled-list/components/controlled-list-item-type-2';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {ShareDesignAccessOpts} from '@Panels/share-panel/components/share-design-access-opts/share-design-access-opts';
import {CircularProgressLoader} from '@Components/circular-progress-loader';
import {InviteState} from '@Panels/share-panel/components/invite-state/invite-state';
import {InviteStateType} from '@Panels/share-panel/components/invite-state/invite-state.types';
import {ShareInTeamBarState} from '@Components/share-in-team-toggle-bar/share-in-team-toggle-bar.types';
import {ShareInTeamToggleBar} from '@Components/share-in-team-toggle-bar/share-in-team-toggle-bar';
import {InputFieldState} from '@Components/input-field-v2/input-field.types';
import {isValidEmail} from '@Utils/string.util';
import {getUserId} from '@Libraries/user.library';
import {closePanel} from '@Components/panel/panel-reducer';
import {useDispatch} from 'react-redux';
import {RecentSuggestionPills} from '@Panels/share-panel/components/recent-suggestion-pills/recent-suggestion-pills';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {UserInfoItem} from '@Components/user-info-item/user-info-item';
import {noop} from '@Utils/general.util';
import {TeamInviteUpsellingCard} from '@Panels/share-panel/components/team-invite-upselling-card/team-invite-upselling-card';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {useShareDesignInTeam} from '@Panels/share-panel/share-panel.hooks';
import {updateIsPosterSharedInTeam} from '@Components/poster-editor/poster-editor-reducer';

export function ShareDesign({itemHashedId, isMobile = false, onShare = noop}: ShareDesignProps): ReactElement<any> {
  const [sharedEditAccessLink, setSharedEditAccessLink] = useState('');
  const [sharedViewAccessLink, setSharedViewAccessLink] = useState('');
  const [sharedLink, setSharedLink] = useState('');
  const [currentEmailAccessType, setCurrentEmailAccessType] = useState<DesignPermissionLevel>(DesignPermissionLevel.CAN_EDIT);
  const [currentLinkAccessType, setCurrentLinkAccessType] = useState<DesignPermissionLevel>(DesignPermissionLevel.CAN_EDIT);
  const [currentUserPermissionLevel, setCurrentUserPermissionLevel] = useState<DesignPermissionLevel>(DesignPermissionLevel.CAN_EDIT);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [recentSuggestions, setRecentSuggestions] = useState<ContributorInfo[]>([]);
  const [recentSuggestionsForPills, setRecentSuggestionsForPills] = useState<ContributorInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmailInviteButtonLoading, setIsEmailInviteButtonLoading] = useState(false);
  const [emailsSentTo, setEmailsSentTo] = useState<string[]>([]);
  const [peopleWithAccess, setPeopleWithAccess] = useState<DesignContributorInfo[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [showInviteSuccess, setShowInviteSuccess] = useState(false);
  const [showInviteError, setShowInviteError] = useState(false);
  const [isInvalidEmail, setIsInvalidEmail] = useState(false);
  const [idDesignOwner, setIdDesignOwner] = useState<number>(0);
  const [showShareInTeamBar, setShowShareInTeamBar] = useState<boolean>(false);
  const [showPremiumTeamUpselling, setShowPremiumTeamUpselling] = useState<boolean>(false);
  const currentUserId = getUserId();
  const dispatch = useDispatch();
  const isPosterMaker = window.PMW.isReactPosterMaker();

  const shareDesignInTeamCommonProps = useShareDesignInTeam(itemHashedId, (barState: ShareInTeamBarState) => {
    onShare(barState === ShareInTeamBarState.ACTIVE || peopleWithAccess.length > 1);
    dispatchShareDesignEventIfApplicable(barState);
    dispatch(updateIsPosterSharedInTeam(barState === ShareInTeamBarState.ACTIVE));
  });

  const dispatchShareDesignEventIfApplicable = (barState: ShareInTeamBarState): void => {
    if (window.PMW.mystuff?.sharedDesignInTeamEvent) {
      document.dispatchEvent(
        new CustomEvent(window.PMW.mystuff.sharedDesignInTeamEvent, {
          detail: {
            itemHashedId: itemHashedId,
            barState: barState,
          },
        })
      );
    }
  };

  useEffect(() => {
    if (!itemHashedId) return;
    setIsLoading(true);
    window.PMW.readLocal('poster/getPosterShareInfo', {posterHashedId: itemHashedId})
      .then((response: ShareDesignInfoResponse) => {
        shareDesignInTeamCommonProps.setSharedInTeamState(getInitialSharedInTeamBarState(response.isSharedInTeam));
        setSharedLink(response.currentUserPermissionLevel === DesignPermissionLevel.CAN_VIEW ? response.shareLinkViewAccess : response.shareLinkEditAccess);
        setSharedEditAccessLink(response.shareLinkEditAccess);
        setSharedViewAccessLink(response.shareLinkViewAccess);
        setCurrentEmailAccessType(response.currentUserPermissionLevel === DesignPermissionLevel.CAN_VIEW ? DesignPermissionLevel.CAN_VIEW : DesignPermissionLevel.CAN_EDIT);
        setCurrentLinkAccessType(response.currentUserPermissionLevel === DesignPermissionLevel.CAN_VIEW ? DesignPermissionLevel.CAN_VIEW : DesignPermissionLevel.CAN_EDIT);
        setCurrentUserPermissionLevel(response.currentUserPermissionLevel);
        setRecentSuggestions(response.previousContributorsInfo);
        setPeopleWithAccess(response.peopleWithAccess);
        setRecentSuggestionsForPills(response.previousContributorsInfo);
        setIdDesignOwner(Number(response.idDesignOwner));
        setShowShareInTeamBar(response.showShareInTeamBar ?? false);
        setShowPremiumTeamUpselling(response.showPremiumTeamUpselling ?? false);
      })
      .catch((errorResponse: unknown) => {
        if (errorResponse && typeof errorResponse === 'object' && 'code' in errorResponse && errorResponse.code === 'cannot_edit_send_invite') {
          openPermissionDeniedPanel(window.i18next.t('pmwjs_design_invite_permission_denied_message'));
          dispatch(closePanel(SHARE_PANEL_ID));
        } else {
          openErrorModal();
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [itemHashedId]);

  const getAccessTypesDropdownContent = (selected: DesignPermissionLevel, onClickHandler: (permissionLevel: DesignPermissionLevel) => void): ReactElement<any> => {
    return (
      <ul className="_unpadded _unmargin" key={`access-type-dropdown-${selected}`}>
        <ControlledListItemType3
          id="edit"
          text={window.i18next.t('pmwjs_can_edit')}
          listSize={ControlledListItemSize.SMALL}
          onClick={() => {
            onClickHandler(DesignPermissionLevel.CAN_EDIT);
          }}
          isSelected={selected === DesignPermissionLevel.CAN_EDIT}
          textClass="capitalize-sentence"
        />
        <ControlledListItemType3
          id="view"
          text={window.i18next.t('pmwjs_can_view')}
          listSize={ControlledListItemSize.SMALL}
          onClick={() => {
            onClickHandler(DesignPermissionLevel.CAN_VIEW);
          }}
          isSelected={selected === DesignPermissionLevel.CAN_VIEW}
          textClass="capitalize-sentence"
        />
      </ul>
    );
  };

  const onClickInvite = (): void => {
    setIsEmailInviteButtonLoading(true);
    if (isPosterMaker) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_DESIGN_INVITEBYEMAIL, {[GA4EventParamName.SOURCE]: 'Editor'});
    } else {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_DESIGN_INVITEBYEMAIL, {[GA4EventParamName.SOURCE]: 'My Stuff'});
    }
    void window.PMW.writeLocal('user/sendContributorInvitations', {
      id: itemHashedId,
      type: ShareItemType.POSTER,
      'emails[]': selectedEmails,
      permissionLevel: currentEmailAccessType,
    })
      .then((response: ContributorEmailInvitationResponse) => {
        setIsEmailInviteButtonLoading(false);
        setEmailsSentTo(response.emailsSentTo);
        setShowInviteSuccess(response.emailsSentTo.length > 0);
        setTimeout(() => {
          setShowInviteSuccess(false);
        }, 5000);
        setSelectedEmails([]);
        updatePeopleWithAccessOnEmailInvite(response.emailsSentTo);
      })
      .catch((response: unknown) => {
        setIsEmailInviteButtonLoading(false);
        openErrorModal({message: getShareErrorMessage(response, 'design')});
      });
  };

  const onEmailsInput = (input: string): void => {
    resetInputState();
    setEmailInput(input);
  };

  const getFilteredSuggestions = (input: string): ContributorInfo[] => {
    const normalized = input.trim().toLowerCase();
    return recentSuggestions.filter((c) => c.email?.toLowerCase().includes(normalized) || c.name?.toLowerCase().includes(normalized));
  };

  const getFilteredSuggestedEmails = (suggestions: ContributorInfo[]): string[] => {
    return suggestions.map((c) => c.email).filter((email): email is string => Boolean(email));
  };

  const getSuggestedPreviewTags = (): ControlledListItemType2Props[] => {
    return getFilteredSuggestions(emailInput).map((c, index) => ({
      id: `controlled-list-item-${index}`,
      text: c.name ?? '',
      subText: c.email,
      thumbnail: {
        type: ThumbnailType.IMAGE,
        imageSrc: getProfilePictureUrl(c),
        className: 'radius-round',
        imageClassName: `radius-round ${styles.profilePicture}`,
      },
    }));
  };

  const addEmailAddress = (emailTagInput: string): void => {
    if (!isValidEmail(emailTagInput)) {
      setIsInvalidEmail(true);
      return;
    } else {
      setSelectedEmails((prevState) => [...prevState, emailTagInput]);
    }
  };

  const onRemoveSelectedEmailAddress = (emailTagInput: string): void => {
    const updatedEmails = selectedEmails.filter((email) => email !== emailTagInput);
    setSelectedEmails(updatedEmails);
    resetInputState();
  };

  const onRemoveEmailAddress = (): void => {
    const updatedEmails = selectedEmails.slice(0, -1);
    setSelectedEmails(updatedEmails);
    resetInputState();
  };

  const getEmailsInputClasses = (): string => {
    return `_full-width flex-v-row _borderless ${styles.suggestionContainer}`;
  };

  const shareViaEmailPermissionChange = (newPermission: DesignPermissionLevel): void => {
    setCurrentEmailAccessType(newPermission);

    const accessTypeValue = newPermission === DesignPermissionLevel.CAN_VIEW ? DesignPermissionLevelValue.VIEW : DesignPermissionLevelValue.EDIT;
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_DESIGN_ACCESSLEVEL, {
      [GA4EventParamName.TYPE]: 'invitebyemail',
      [GA4EventParamName.VALUE]: accessTypeValue,
      [GA4EventParamName.SOURCE]: isPosterMaker ? 'Editor' : 'My Stuff',
    });
  };

  const shareLinkPermissionChange = (newPermission: DesignPermissionLevel): void => {
    setCurrentLinkAccessType(newPermission);

    if (newPermission === DesignPermissionLevel.CAN_VIEW) {
      setSharedLink(sharedViewAccessLink);

      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_DESIGN_ACCESSLEVEL, {
        [GA4EventParamName.TYPE]: 'invitelink',
        [GA4EventParamName.VALUE]: DesignPermissionLevelValue.VIEW,
        [GA4EventParamName.SOURCE]: isPosterMaker ? 'Editor' : 'My Stuff',
      });
    } else {
      setSharedLink(sharedEditAccessLink);

      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_DESIGN_ACCESSLEVEL, {
        [GA4EventParamName.TYPE]: 'invitelink',
        [GA4EventParamName.VALUE]: DesignPermissionLevelValue.EDIT,
        [GA4EventParamName.SOURCE]: isPosterMaker ? 'Editor' : 'My Stuff',
      });
    }
  };

  const emailPopup = useMemo((): ReactElement<any> => {
    return getAccessTypesDropdownContent(currentEmailAccessType, shareViaEmailPermissionChange);
  }, [currentEmailAccessType, setCurrentEmailAccessType]);

  const linkPopup = useMemo((): ReactElement<any> => {
    return getAccessTypesDropdownContent(currentLinkAccessType, shareLinkPermissionChange);
  }, [currentLinkAccessType, shareLinkPermissionChange]);

  const getPopupClassName = (): string => {
    return `${styles.dropdownDesign} _unpadded-bottom`;
  };

  const getPopupSelectorClassName = (): string => {
    return `${styles.dropdownSelector} ${styles.small}`;
  };

  const onRecentSuggestionPillClick = (text: string, email: string): void => {
    if (isPosterMaker) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_DESIGN_INVITEBYEMAIL_RECOMMENDATION, {[GA4EventParamName.SOURCE]: 'Editor'});
    } else {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_DESIGN_INVITEBYEMAIL_RECOMMENDATION, {[GA4EventParamName.SOURCE]: 'My Stuff'});
    }
    setSelectedEmails((prevSelections) => [...prevSelections, email]);
  };

  const getPeopleWithAccess = (): ReactElement<any> | null => {
    return shouldShowOwnerRow() ? (
      <div className="flex-v-row">
        <Text
          val={`${isMobile ? window.i18next.t('pmwjs_who_has_access') : window.i18next.t('pmwjs_people_with_access')} (${peopleWithAccess.length})`}
          bold
          className="spacing-m-b-2 content-body"
          size={TextSize.XSMALL}
        />
        <div className={`flex-v-row ${styles.peopleWithAccessList}`}>
          {peopleWithAccess.filter((item) => !(item.isOwner && !shouldShowOwnerRow())).map((item: DesignContributorInfo, index) => getContributorInfoOpts(item, index))}
        </div>
      </div>
    ) : null;
  };

  const getContributorInfoOpts = (item: DesignContributorInfo, index: number): ReactElement<any> => {
    return (
      <UserInfoItem
        key={`controlled-list-item-${index}`}
        profilePictureURL={getProfilePictureUrl(item)}
        primaryText={getContributorInfoText(item)}
        secondaryText={getContributorInfoSecondaryText(item)}
        subText={getContributorInfoSubText(item)}
        actions={
          <ShareDesignAccessOpts
            key={item.idUser}
            posterHashedId={itemHashedId}
            designerContributorInfo={item}
            onPermissionLevelUpdate={onCollaboratorPermissionLevelUpdate}
            onRemoveUser={onRemoveCollaborator}
            onResendInvite={onResendInvite}
            isMobile={isMobile}
            idDesignOwner={idDesignOwner}
          />
        }
      />
    );
  };

  const shouldShowOwnerRow = (): boolean => {
    return peopleWithAccess.length > 1;
  };

  const onCollaboratorPermissionLevelUpdate = (updatedPermissionLevel: number, idUser: number | undefined): void => {
    setPeopleWithAccess((prev) => prev.map((person) => (person.idUser === idUser ? {...person, permissionLevel: updatedPermissionLevel} : person)));
  };

  const onRemoveCollaborator = (idUser: number | undefined): void => {
    const updatedPeople = peopleWithAccess.filter((person) => person.idUser !== idUser);

    if (updatedPeople.length === 1) {
      onShare(false);
    }

    setPeopleWithAccess(updatedPeople);
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_DESIGN_ACCESSLEVEL, {
      [GA4EventParamName.VALUE]: 'remove',
      [GA4EventParamName.SOURCE]: isPosterMaker ? 'Editor' : 'My Stuff',
    });
    if (window.location.pathname.includes('/posterbuilder') && idUser === currentUserId) {
      window.open(window.PMW.util.site_url('posters/mine#/shared'), '_self');
    } else {
      if (idDesignOwner !== currentUserId) {
        dispatch(closePanel(SHARE_PANEL_ID));
      }
    }
  };

  const onResendInvite = (email: string, inviteStateType: InviteStateType): void => {
    setEmailsSentTo([email]);
    if (inviteStateType === InviteStateType.ERROR) {
      setShowInviteError(true);
    } else {
      setShowInviteSuccess(true);
    }

    setTimeout(() => {
      setShowInviteError(false);
      setShowInviteSuccess(false);
    }, 5000);
  };

  const updatePeopleWithAccessOnEmailInvite = (emailsSentTo: string[]): void => {
    emailsSentTo.forEach((email) => {
      const isEmailExists = peopleWithAccess.some((person) => person.email === email);
      if (!isEmailExists) {
        addPersonToPeopleWithAccess({
          permissionLevel: DesignPermissionLevel.INVITE_SENT,
          email: email,
        });
      }
    });
  };

  const addPersonToPeopleWithAccess = (newPerson: DesignContributorInfo): void => {
    setPeopleWithAccess((prev) => [...prev, newPerson]);
  };

  const getContributorInfoText = (contributorInfo: DesignContributorInfo): string => {
    if (contributorInfo.permissionLevel === DesignPermissionLevel.INVITE_SENT) {
      return contributorInfo.email ?? '';
    }

    if (contributorInfo.name && contributorInfo.idUser === currentUserId) {
      return `${contributorInfo.name} (${window.i18next.t('pmwjs_you')})`;
    }

    return contributorInfo.name ?? contributorInfo.email ?? '';
  };

  const getContributorInfoSubText = (contributorInfo: DesignContributorInfo): string => {
    return contributorInfo.permissionLevel === DesignPermissionLevel.INVITE_SENT ? '' : (contributorInfo.email ?? '');
  };

  const getContributorInfoSecondaryText = (contributorInfo: DesignContributorInfo): string => {
    return contributorInfo.permissionLevel === DesignPermissionLevel.INVITE_SENT ? `(${window.i18next.t('pmwjs_invite_sent')})` : '';
  };

  const getSuggestedEmails = (): string[] => {
    const suggestionsForEmail = getFilteredSuggestions(emailInput);
    return getFilteredSuggestedEmails(suggestionsForEmail);
  };

  const getInviteResentState = (): ReactElement<any> | null => {
    return showInviteSuccess ? (
      <div>
        <InviteState inviteesList={emailsSentTo} type={InviteStateType.SUCCESS} />
      </div>
    ) : null;
  };

  const getErrorState = (): ReactElement<any> | null => {
    return showInviteError ? (
      <div>
        <InviteState inviteesList={emailsSentTo} type={InviteStateType.ERROR} />
      </div>
    ) : null;
  };

  const resetInputState = (): void => {
    if (isInvalidEmail) {
      setIsInvalidEmail(false);
    }
  };

  const isInviteButtonDisabled = selectedEmails.length === 0;

  const getInviteButtonForMobile = (): ReactElement<any> | undefined => {
    return isMobile ? (
      <Button
        text={window.i18next.t('pmwjs_invite')}
        size={Size.SMALL}
        icon="icon-group-add"
        iconPlacement={IconPlacement.LEFT}
        type={Type.PRIMARY}
        customClasses={`_full-width ${styles.panelButton}`}
        onClick={onClickInvite}
        isLoading={isLoading}
        disabled={isInviteButtonDisabled}
      />
    ) : undefined;
  };

  const onCopyLink = (): void => {
    if (isPosterMaker) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_DESIGN_INVITELINK, {[GA4EventParamName.SOURCE]: 'Editor'});
    } else {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_DESIGN_INVITELINK, {[GA4EventParamName.SOURCE]: 'My Stuff'});
    }
  };

  const getRecentSuggestionPills = (): ContributorInfo[] => {
    return recentSuggestionsForPills
      .filter((item) => {
        return !selectedEmails.includes(item.email ?? '');
      })
      .slice(0, 3);
  };

  const getPremiumTeamUpselling = (): ReactElement<any> | undefined => {
    return showPremiumTeamUpselling ? (
      <>
        <TeamInviteUpsellingCard isMobile={isMobile} />
        {shouldShowOwnerRow() ? (
          <div>
            <Divider />
          </div>
        ) : undefined}
      </>
    ) : undefined;
  };

  return (
    <>
      {isLoading ? (
        <div className={`${styles.loaderContainer} flex-center _full-width _full-height`}>
          <CircularProgressLoader classes="_unmargin" />
        </div>
      ) : (
        <>
          {showShareInTeamBar ? (
            <ShareInTeamToggleBar
              state={shareDesignInTeamCommonProps.sharedInTeamState}
              onStateUpdate={(state) => {
                void shareDesignInTeamCommonProps.onShareTeamToggle(state);
              }}
              isCurrentlyLoading={shareDesignInTeamCommonProps.isShareInTeamLoading}
            />
          ) : undefined}
          <div>
            <ShareViaEmail
              permissionLevelLabel={getDesignPermissionLevelText(currentEmailAccessType)}
              accessDropdownOptions={emailPopup}
              onClickInvite={onClickInvite}
              allSuggestedTags={getSuggestedEmails()}
              allSelectedTags={selectedEmails}
              onInputChangeCallback={onEmailsInput}
              inputClassName={getEmailsInputClasses()}
              onSaveTagCallback={addEmailAddress}
              onPillClick={onRemoveSelectedEmailAddress}
              onRemoveTag={onRemoveEmailAddress}
              popupClassName={getPopupClassName()}
              popupSelectorClassName={getPopupSelectorClassName()}
              isLoading={isEmailInviteButtonLoading}
              isInviteButtonDisabled={isInviteButtonDisabled}
              showPreviewTags={true}
              previewTagsDetail={getSuggestedPreviewTags()}
              doClosePopupOnInnerClick
              inputFieldWrapperState={isInvalidEmail ? InputFieldState.ERROR : InputFieldState.DEFAULT}
              inputWrapperInfoText={isInvalidEmail ? window.i18next.t('pmwjs_invalid_email_address') : ''}
              retainInputOnError
              isMobile={isMobile}
              isSelectorDisabled={currentUserPermissionLevel === DesignPermissionLevel.CAN_VIEW}
            />
            <RecentSuggestionPills
              recentSuggestions={getRecentSuggestionPills()}
              showScrollIcons
              onRecentSuggestionPillClick={(name, email) => {
                onRecentSuggestionPillClick(name, email);
              }}
              showTooltip={!isMobile}
            />
          </div>
          {getInviteButtonForMobile()}
          <ShareViaLink
            sharedLink={sharedLink}
            placeholderText="https://www.postermywall.com/index.php/poster/contribute/..."
            itemPermissionLevel={getDesignPermissionLevelText(currentLinkAccessType)}
            accessDropdownOptions={linkPopup}
            popupClassName={getPopupClassName()}
            popupSelectorClassName={getPopupSelectorClassName()}
            inputFieldClassName="flex-1"
            doClosePopupOnInnerClick
            isMobile={isMobile}
            onCopyLink={onCopyLink}
            isSelectorDisabled={currentUserPermissionLevel === DesignPermissionLevel.CAN_VIEW}
          />
          {emailsSentTo.length > 0 || shouldShowOwnerRow() || showPremiumTeamUpselling ? (
            <div>
              {emailsSentTo.length > 0 || shouldShowOwnerRow() || showPremiumTeamUpselling ? <Divider /> : null}
              {getInviteResentState()}
              {getErrorState()}
            </div>
          ) : null}
          {getPremiumTeamUpselling()}
          {getPeopleWithAccess()}
        </>
      )}
    </>
  );
}
